import type { ProductDetail } from "@viajero/proto/guest/v1";
import type { CourseBookingSelection } from "@/lib/courseBookingContext";
import { resolvePartySizeRange } from "@/lib/partySizeRange";
import { isSelectableTime } from "@/lib/turnStartTimes";

/**
 * コース詳細画面で選択した予約内容 (利用日・時間・人数。人数は商品の枚数下限上限と
 * 店舗の予約可能人数を合成した実効範囲で判定し、時間は店舗の回転設定 on なら開始時間に
 * 一致することも求める) が
 * 「予約情報入力に進む」/「決済に進む」/メールアドレス認証を許可できる状態かどうかを判定する。
 * 同意チェック (利用規約・プライバシー・キャンセルポリシー) は購入者情報入力画面
 * (BuyerInfoStep) の「決済に進む」でのみ検証するため、ここには含めない。
 * コース詳細画面 (CourseDetailView) と購入者情報入力画面 (BuyerInfoStep) の双方で
 * 同じ条件を満たす必要がある (BuyerInfoStep は URL 直打ちなどでコース詳細画面を経由せず
 * 遷移してくる可能性があるため、購入者情報とは独立して検証する)。
 *
 * 利用日はカレンダーの min 属性で過去日を選べないようにしているが、preferredDate の
 * 自動入力など別経路で過去日がセットされる可能性があるため、minDate (本日, "YYYY-MM-DD")
 * 以降であることをここでも検証する。日付は "YYYY-MM-DD" のため文字列比較で判定できる。
 */
export function isBookingSelectionValid(
  selection: CourseBookingSelection,
  product: Pick<
    ProductDetail,
    | "stockNumberLimitMax"
    | "stockNumberLimitMin"
    | "shopMinPartySize"
    | "shopMaxPartySize"
    | "shopTurnEnabled"
    | "shopTurnStartTimes"
  >,
  minDate: string,
): boolean {
  const totalQuantity = Object.values(selection.quantities).reduce((sum, n) => sum + n, 0);
  const range = resolvePartySizeRange(product);
  const withinStockLimit =
    totalQuantity > 0 &&
    !range.unavailable &&
    (range.max === 0 || totalQuantity <= range.max) &&
    (range.min === 0 || totalQuantity >= range.min);

  return (
    Boolean(selection.date) &&
    selection.date >= minDate &&
    isSelectableTime(product, selection.time) &&
    withinStockLimit
  );
}
